// Pure logic for finding + merging duplicate lead cards. No React, no Firestore.
// Mirrors the planStatusCleanup pattern in statusSheetData.js (pure + tested).
import { normalizeAddressKey } from "./statusSheetData";
import { LISTING_STAGES } from "./listingLeadStages";
import { BUYER_STAGE_ORDER, normalizeStage as normalizeBuyerStage } from "./buyerLeadStages";

const LISTING_COLLECTION = "listing_leads";
const BUYER_COLLECTION = "buyer_leads";
const RESOLVED = ["won", "lost", "converted"];

const notDeleted = (x) => !x?.deletedAt && x?.deleted !== true;
const isActive = (x) => {
  const s = String(x?.status || "active").toLowerCase();
  return !RESOLVED.includes(s) && !x?.convertedDealId;
};
const eligible = (x) => notDeleted(x) && isActive(x);

// Name identity for de-dup: lowercase, punctuation/whitespace collapsed.
export const normalizeNameKey = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Rank a stage by its position in the pipeline's real stage order (later =
// further along). Case-insensitive; unknown/blank = -1. Each pipeline has its
// OWN vocabulary, so the caller passes the matching order.
const rankIn = (order, stage) => {
  const s = String(stage || "").toLowerCase();
  return order.findIndex((x) => String(x).toLowerCase() === s);
};
const listingStageRank = (l) => rankIn(LISTING_STAGES, l?.stage);
// Buyer stages were renamed over time (Contacted → Nurturing, etc.); normalize
// old aliases before ranking so a legacy-stage card isn't treated as unknown.
const buyerStageRank = (l) => rankIn(BUYER_STAGE_ORDER, normalizeBuyerStage(String(l?.stage || "")));

// Pick the strongest lead: furthest stage > most recent createdAt.
// (Leads with a convertedDealId are already excluded by `eligible`, so a
// deal-link tie-breaker would never fire — kept out to avoid dead logic.)
function pickSuggested(members, stageRank) {
  return members
    .slice()
    .sort((a, b) => {
      const stage = stageRank(b) - stageRank(a);
      if (stage) return stage;
      return Number(b.createdAt || 0) - Number(a.createdAt || 0);
    })[0].id;
}

// A listing lead's cluster keys (it may share several). We union groups that
// share ANY key, so a lead joined by contact OR name+address isn't double-listed.
function listingKeys(l) {
  const keys = [];
  if (l.contactId) keys.push(`contact:${l.contactId}`);
  const nameKey = normalizeNameKey(l.contactName);
  const addrKey = normalizeAddressKey(l.propertyAddress);
  if (nameKey && addrKey) keys.push(`na:${nameKey}|${addrKey}`);
  return keys;
}

// Union-find over leads: any two leads sharing a key end up in one cluster.
function unionClusters(leads, keysFn, pipeline, collection, stageRank) {
  const parent = new Map(); // leadId -> leadId
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const eligibleLeads = leads.filter(eligible);
  eligibleLeads.forEach((l) => parent.set(l.id, l.id));
  const keyToLead = new Map();
  eligibleLeads.forEach((l) => {
    keysFn(l).forEach((k) => {
      if (keyToLead.has(k)) {
        parent.set(find(l.id), find(keyToLead.get(k)));
      } else {
        keyToLead.set(k, l.id);
      }
    });
  });
  const byRoot = new Map();
  eligibleLeads.forEach((l) => {
    const r = find(l.id);
    if (!byRoot.has(r)) byRoot.set(r, []);
    byRoot.get(r).push(l);
  });
  const out = [];
  for (const [, members] of byRoot) {
    if (members.length < 2) continue;
    // Key by the cluster's member ids (sorted), NOT the union-find root. The
    // root is an internal artifact that depends on document order, so keying by
    // it would let an admin's per-cluster choice mis-map if the data shifts
    // between the preview and Apply. Sorted member ids are stable for the same
    // set of duplicates.
    const memberIds = members.map((m) => m.id).slice().sort();
    out.push({
      key: `${pipeline}:${memberIds.join(",")}`,
      pipeline,
      collection,
      leads: members,
      suggestedSurvivorId: pickSuggested(members, stageRank),
    });
  }
  return out;
}

export function planDuplicateMerge({ listingLeads = [], buyerLeads = [] } = {}) {
  const listing = unionClusters(listingLeads, listingKeys, "listing", LISTING_COLLECTION, listingStageRank);
  const buyer = unionClusters(
    buyerLeads,
    (l) => (l.contactId ? [`contact:${l.contactId}`] : []),
    "buyer",
    BUYER_COLLECTION,
    buyerStageRank,
  );
  return { clusters: [...listing, ...buyer] };
}

// Fold a cluster into the chosen survivor. Pure — returns the patch + the ids
// to archive. coClients: union of the copies' EXISTING coClients arrays (dedup
// by contactId), minus the survivor's own primary. Archived copies' PRIMARY
// contacts are NOT promoted — in a name+address cluster they are duplicate
// contact records of the same person, not extra people.
export function computeMerge(cluster, survivorId) {
  const leads = cluster.leads || [];
  const survivor = leads.find((l) => l.id === survivorId);
  // The chosen survivor is not in this cluster — the data shifted between the
  // preview and Apply. Return null so the caller SKIPS this cluster rather than
  // silently archiving cards around a fallback survivor the admin never picked.
  if (!survivor) return null;
  const others = leads.filter((l) => l.id !== survivor.id);
  const ordered = [survivor, ...others];

  const seen = new Set([survivor.contactId].filter(Boolean));
  const coClients = [];
  ordered.forEach((l) => {
    (Array.isArray(l.coClients) ? l.coClients : []).forEach((c) => {
      if (c && c.contactId && !seen.has(c.contactId)) {
        seen.add(c.contactId);
        coClients.push({ contactId: c.contactId, contactName: c.contactName || "" });
      }
    });
  });

  // Survivor's notes first, then the rest; drop empty and identical blocks so
  // duplicate cards that share the same note text don't repeat it.
  const seenNotes = new Set();
  const notes = ordered
    .map((l) => String(l.notes || "").trim())
    .filter((n) => n && !seenNotes.has(n) && seenNotes.add(n))
    .join("\n— merged —\n");

  return { survivorId: survivor.id, survivorPatch: { coClients, notes }, archivedIds: others.map((l) => l.id) };
}
